import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import MergeOutlinedIcon from '@mui/icons-material/MergeOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined';
import type { ConflictDoc, PendingConflict } from './conflictHandler';
import ConflictFieldRow, { type ConflictSide } from './ConflictFieldRow';
import {
    describeConflictRecord,
    labelPrefixFor,
    resolveEntityLabel
} from './conflictLabels';
import { conflictRows, type ConflictRow } from './conflictRows';
import { useConflictValueText } from './useConflictValueText';
import { useActorLabel } from '../users/useActorLabel';
import { resolveFieldLabel } from '../../i18n/fieldLabel';

interface ConflictDialogProps {
    conflict: PendingConflict;
    onResolve: (conflictId: string, resolved: ConflictDoc) => void;
}

export default function ConflictDialog({
    conflict,
    onResolve
}: ConflictDialogProps) {
    const intl = useIntl();
    const actorLabel = useActorLabel();

    // Somebody deleted this record while we were editing it. The generic
    // "keep mine / keep theirs" framing lies here: "theirs" is a tombstone, and
    // "mine" would put the record back for EVERYONE without ever saying so.
    // Both outcomes are still available, just named for what they do.
    const deletedRemotely =
        conflict.remote._deleted && !conflict.local._deleted;
    const deletedBy = actorLabel(
        typeof conflict.remote.updatedBy === 'string'
            ? conflict.remote.updatedBy
            : undefined
    );
    const valueText = useConflictValueText(conflict);
    const rows = useMemo(
        () => conflictRows(conflict.entityType, conflict.conflicts),
        [conflict.entityType, conflict.conflicts]
    );
    // Per row, which side it keeps; the server's until somebody chooses.
    const [sides, setSides] = useState<Record<string, ConflictSide>>({});
    const sideOf = (labelField: string) => sides[labelField] ?? 'remote';

    /**
     * A field as one side holds it. Read from the documents, not the conflict
     * list: a person row also carries the twin that merged on its own.
     */
    function valueOn(field: string, side: ConflictSide): unknown {
        return side === 'local'
            ? conflict.local[field]
            : conflict.remote[field];
    }

    const resolvedFields: Record<string, unknown> = {};
    for (const row of rows) {
        for (const field of row.fields) {
            resolvedFields[field] = valueOn(field, sideOf(row.labelField));
        }
    }

    function rowText(row: ConflictRow, side: ConflictSide): string {
        if (row.person) {
            return valueText.person(
                row.person,
                valueOn(row.person.name, side),
                valueOn(row.person.id, side)
            );
        }
        return valueText.text(row.labelField, valueOn(row.labelField, side));
    }

    function handleResolve() {
        const resolved: ConflictDoc = {
            ...conflict.merged,
            ...resolvedFields,
            updatedAt: new Date().toISOString()
        };

        onResolve(conflict.id, resolved);
    }

    function handleKeepLocal() {
        onResolve(conflict.id, {
            ...conflict.local,
            updatedAt: new Date().toISOString()
        });
    }

    function handleKeepRemote() {
        onResolve(conflict.id, {
            ...conflict.remote,
            updatedAt: new Date().toISOString()
        });
    }

    /** Let the deletion stand. Adopting the tombstone unchanged means the local
     *  copy simply matches the server again and nothing is pushed back. */
    function handleAcceptDelete() {
        onResolve(conflict.id, conflict.remote);
    }

    /** Bring the record back, carrying the field choices made above — the same
     *  merge as `handleResolve`, only alive. */
    function handleRestore() {
        onResolve(conflict.id, {
            ...conflict.merged,
            ...resolvedFields,
            _deleted: false,
            updatedAt: new Date().toISOString()
        });
    }

    const entityLabel = resolveEntityLabel(intl, conflict.entityType);
    const identifier =
        valueText.record ||
        describeConflictRecord(
            intl,
            conflict.entityType,
            conflict.local,
            conflict.remote
        );

    return (
        <Dialog open maxWidth="md" fullWidth>
            <DialogTitle>
                <FormattedMessage
                    id={
                        deletedRemotely
                            ? identifier
                                ? 'conflict.deletedTitleWithId'
                                : 'conflict.deletedTitle'
                            : identifier
                              ? 'conflict.titleWithId'
                              : 'conflict.title'
                    }
                    values={{ entity: entityLabel, identifier }}
                />
            </DialogTitle>
            <DialogContent>
                {deletedRemotely && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <FormattedMessage
                            id={
                                deletedBy
                                    ? 'conflict.deletedByUser'
                                    : 'conflict.deletedByUnknown'
                            }
                            values={{ name: deletedBy, entity: entityLabel }}
                        />
                    </Alert>
                )}
                {rows.map((row) => (
                    <ConflictFieldRow
                        key={row.labelField}
                        label={resolveFieldLabel(
                            intl,
                            labelPrefixFor(conflict.entityType),
                            row.labelField
                        )}
                        isRichText={row.fields.some((field) =>
                            conflict.richTextFields.includes(field)
                        )}
                        localText={rowText(row, 'local')}
                        remoteText={rowText(row, 'remote')}
                        selected={sideOf(row.labelField)}
                        onChange={(side) =>
                            setSides((previous) => ({
                                ...previous,
                                [row.labelField]: side
                            }))
                        }
                    />
                ))}
            </DialogContent>
            <DialogActions>
                {deletedRemotely ? (
                    // Two outcomes, both named. Restoring is the deliberate one
                    // and stays secondary: it undoes someone else's deletion.
                    <>
                        <Button
                            onClick={handleRestore}
                            variant="outlined"
                            color="warning"
                            startIcon={<RestoreFromTrashOutlinedIcon />}
                        >
                            <FormattedMessage id="conflict.restore" />
                        </Button>
                        <Button
                            onClick={handleAcceptDelete}
                            variant="contained"
                            startIcon={<DeleteOutlinedIcon />}
                        >
                            <FormattedMessage id="conflict.acceptDelete" />
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            onClick={handleKeepLocal}
                            variant="outlined"
                            color="info"
                            startIcon={<SmartphoneOutlinedIcon />}
                        >
                            <FormattedMessage id="conflict.keepLocal" />
                        </Button>
                        <Button
                            onClick={handleKeepRemote}
                            variant="outlined"
                            color="warning"
                            startIcon={<CloudOutlinedIcon />}
                        >
                            <FormattedMessage id="conflict.keepRemote" />
                        </Button>
                        <Button
                            onClick={handleResolve}
                            variant="contained"
                            startIcon={<MergeOutlinedIcon />}
                        >
                            <FormattedMessage id="conflict.resolve" />
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
